import { create } from 'zustand';
import { MediaDirectory } from '../types/media-types';

export enum MediaMimeType {
  ALL = 'all',
  IMAGE = 'image',
  VIDEO = 'video',
  AUDIO = 'audio'
}

interface MediaDirectoryState {
  currentDirectoryId: string | null;
  apiPath: string | null;
  displayPath: string | null;
  isCreatingDirectory: boolean;
  newDirectoryName: string;
  mimeTypeFilter: MediaMimeType;
  // Flag to track if a directory was clicked directly (not through back button or nested navigation)
  directClicked: boolean;
  // Map of directory IDs to their full hierarchical paths, critical for accurate navigation
  directoryPathMap: Record<string, string>;
  // Track which directories are expanded in the directory tree
  expandedDirectoryIds: string[];

  // Search query for media filtering
  searchQuery: string;
  
  // Counter for tracking refreshes - used to bust cache after uploads
  refreshCounter: number;

  // Loading state for search
  loadingSearch: boolean;
  
  // Actions
  setCurrentDirectory: (directoryId: string | null, apiPath?: string | null) => void;
  setIsCreatingDirectory: (isCreating: boolean) => void;
  setNewDirectoryName: (name: string) => void;
  resetNewDirectoryForm: () => void;
  setMimeTypeFilter: (mimeType: MediaMimeType) => void;
  // Action to track direct directory clicks vs. other navigation
  setDirectClicked: (clicked: boolean) => void;
  // Force reset of directory path to override any cached paths
  forceDirectoryPath: (path: string | null) => void;
  // Set/update directory path mappings to maintain proper hierarchy
  setDirectoryPathMap: (updater: (map: Record<string, string>) => Record<string, string>) => void;
  // Directory expansion functions for tree view
  setDirectoryExpanded: (directoryId: string, expanded: boolean) => void;
  expandParentDirectories: (directoryId: string, allDirectories: any[]) => void;
  // Set search query for media filtering
  setSearchQuery: (query: string) => void;
  // Increment refresh counter to bust cache for media refresh
  incrementRefreshCounter: () => void;

  // Set loading state for search
  setLoadingSearch: (loading: boolean) => void;
}

// Helper function to clean directory prefixes from display paths
const cleanDisplayPath = (path: string): string => {
  if (!path) return path;
  
  // Split path into segments
  const segments = path.split('/');
  
  // Clean each segment of internal prefixes
  const cleanedSegments = segments.map(segment => {
    if (!segment) return segment; // Keep empty segments (for leading/trailing slashes)
    
    // Remove dir-root- prefix
    if (segment.startsWith('dir-root-')) {
      return segment.substring('dir-root-'.length);
    }
    
    // Remove dir-child- prefix
    if (segment.startsWith('dir-child-')) {
      const match = segment.match(/^dir-child-(.+)$/);
      if (match && match[1]) {
        return match[1];
      }
    }
    
    // Remove any other dir- prefixes generically
    if (segment.startsWith('dir-')) {
      const prefixMatch = segment.match(/^dir-(?:child|root|[^-]+)-(.+)$/);
      if (prefixMatch && prefixMatch[1]) {
        return prefixMatch[1];
      }
    }
    
    return segment;
  });
  
  return cleanedSegments.join('/');
};

export const useMediaDirectoryStore = create<MediaDirectoryState>((set) => ({
  currentDirectoryId: null,
  apiPath: null,
  displayPath: null,
  isCreatingDirectory: false,
  newDirectoryName: '',
  mimeTypeFilter: MediaMimeType.ALL,
  // Initialize the directClicked flag to false by default
  directClicked: false,
  // Initialize the directory path map as an empty object
  directoryPathMap: {},
  // Initialize with an empty array of expanded directories
  expandedDirectoryIds: [],
  // Initialize with empty search query
  searchQuery: '',
  // Initialize refresh counter for cache busting
  refreshCounter: 0,
  loadingSearch: false,
  
  setCurrentDirectory: (directoryId, apiPath) => set(state => {
    // CRITICAL FIX: Ensure we properly handle nested directory paths for both display and API calls
    let newApiPath = null;
    let newDisplayPath = null;
    
    // Start with a clean slate for root navigation
    if (directoryId === null) {
      return {
        currentDirectoryId: null,
        apiPath: null,
        displayPath: '/'
      };
    }
    
    // CASE 1: An explicit API path was provided - use it directly (highest priority)
    // This happens when we use our special handler for nested directories
    if (apiPath) {
      // CRITICAL FIX: Handle paths correctly for nested directories
      // 1. Store the exact apiPath (without leading slash) for API requests
      newApiPath = apiPath;
      
      // 2. Create the display path WITH exactly one leading slash and clean prefixes
      // Fix for triple slash issue - normalize the path to have exactly one leading slash
      const rawDisplayPath = '/' + apiPath.replace(/^\/*/, '');
      newDisplayPath = cleanDisplayPath(rawDisplayPath);
    }
    // CASE 2: Directory ID contains slashes - it's a nested path
    else if (directoryId && directoryId.includes('/')) {
      newApiPath = directoryId;
      
      const rawDisplayPath = '/' + directoryId.replace(/^\/*/, '');
      newDisplayPath = cleanDisplayPath(rawDisplayPath);
    }
    // CASE 3: Standard directory ID with possible prefixes
    else {
      // Clean any prefixes but keep the directory name
      let dirName = directoryId;
      if (directoryId.startsWith('dir-root-')) {
        dirName = directoryId.substring('dir-root-'.length);
      } else if (directoryId.startsWith('dir-child-')) {
        const match = directoryId.match(/^dir-child-(.+)$/);
        if (match && match[1]) {
          dirName = match[1];
        }
      }
      
      // CRITICAL FIX: Check for path in directoryPathMap to preserve full hierarchy
      if (state.directoryPathMap && state.directoryPathMap[directoryId]) {
        // Use the stored full path from path map to ensure we keep the full hierarchy
        newApiPath = state.directoryPathMap[directoryId];
        const rawDisplayPath = `/${newApiPath}`;
        newDisplayPath = cleanDisplayPath(rawDisplayPath);
      } else {
        // Fall back to just the directory name if no mapping exists
        newApiPath = dirName;
        newDisplayPath = `/${dirName}`;
      }
    }
    
    
    // Use the directClicked flag to decide whether to use clean paths
    // Then automatically reset it to false for next navigation
    const currentDirectClicked = state.directClicked;
    
    return {
      currentDirectoryId: directoryId,
      apiPath: newApiPath,
      displayPath: newDisplayPath,
      // Critical: Reset the directClicked flag after using it
      // This ensures it's only true for the exact API call following a direct directory click
      directClicked: false
    };
  }),
  setIsCreatingDirectory: (isCreating) => set({ isCreatingDirectory: isCreating }),
  setNewDirectoryName: (name) => set({ newDirectoryName: name }),
  resetNewDirectoryForm: () => set({ isCreatingDirectory: false, newDirectoryName: '' }),
  setMimeTypeFilter: (mimeType) => set({ mimeTypeFilter: mimeType }),
  
  // Set search query
  setSearchQuery: (query: string) => set(state => {
    return { searchQuery: query };
  }),
  
  // New function to track when directories are directly clicked vs other navigation methods
  setDirectClicked: (clicked) => set({ directClicked: clicked }),
  
  // Emergency override to force reset a directory path (used to fix navigation issues)
  forceDirectoryPath: (path) => set({ 
    apiPath: path,
    directClicked: true, // Also ensure direct clicked flag is set when forcing path
  }),
  
  // Add function to update directory path mappings
  setDirectoryPathMap: (updater) => set(state => ({
    directoryPathMap: updater(state.directoryPathMap),
  })),
  
  // Functions to handle directory expansion state
  setDirectoryExpanded: (directoryId: string, expanded: boolean) => set(state => {
    const newExpandedIds = expanded 
      ? [...state.expandedDirectoryIds, directoryId]
      : state.expandedDirectoryIds.filter(id => id !== directoryId);
    
    return { expandedDirectoryIds: newExpandedIds };
  }),
  
  // Track full parent-child paths for selected directories
  expandParentDirectories: (directoryId: string, allDirectories: any[]) => set(state => {
    // Always expand the target directory itself
    
    // Find the selected directory
    const selectedDir = allDirectories.find(dir => dir.id === directoryId);
    if (!selectedDir) {
      return {};
    }
    
    // Method 1: Using parent_directory_id or parent_id fields directly
    function findParentsById(dir: any, parents: string[] = []): string[] {
      if (!dir) return parents;
      
      // Get the parent ID using either field that might be available
      const parentId = dir.parent_directory_id || dir.parent_id;
      if (!parentId) return parents;
      
      // Find this parent in the directory list
      const parentDir = allDirectories.find(d => d.id === parentId);
      if (parentDir) {
        parents.push(parentDir.id);
        // Recursively find this parent's parent
        return findParentsById(parentDir, parents);
      }
      
      // Also try to find using secondary IDs for dirs that have format like "dir-child-ttt/ppp"
      if (parentId.includes('/')) {
        const potentialParentName = parentId.split('/')[0].replace('dir-child-', '');
        const parentByName = allDirectories.find(d => 
          d.name === potentialParentName || 
          d.id === potentialParentName ||
          d.id.includes(potentialParentName));
          
        if (parentByName) {
          parents.push(parentByName.id);
          return findParentsById(parentByName, parents);
        }
      }
      
      return parents;
    }
    
    // Method 2: Using path arrays from custom_properties
    function findParentsByPath(dir: any, parents: string[] = []): string[] {
      if (!dir || !dir.custom_properties?.path) return parents;
      
      const pathSegments = dir.custom_properties.path as string[];
      if (!pathSegments || pathSegments.length <= 1) return parents;
      
      // For each directory, check if it's a parent based on path segments
      allDirectories.forEach(potentialParent => {
        // Skip if it's the same directory or already in parents
        if (potentialParent.id === dir.id || parents.includes(potentialParent.id)) return;
        
        const parentPath = potentialParent.custom_properties?.path as string[] | undefined;
        if (!parentPath) return;
        
        // A directory is a parent if its path segments are a prefix of the target directory's path
        const isParent = parentPath.length < pathSegments.length && 
          parentPath.every((segment, index) => segment === pathSegments[index]);
        
        if (isParent) {
          parents.push(potentialParent.id);
        }
      });
      
      return parents;
    }
    
    // Method 3: Using name paths to find parents
    function findParentsByName(dir: any, parents: string[] = []): string[] {
      if (!dir || !dir.name) return parents;
      
      // If this directory has a path like "parent/child", we need to find the parent
      const dirName = dir.name as string;
      if (!dirName.includes('/')) return parents;
      
      const pathParts = dirName.split('/');
      if (pathParts.length <= 1) return parents;
      
      // We need to find a directory that matches the parent part of the path
      const parentName = pathParts.slice(0, -1).join('/');
      
      // Look for a directory with this name
      const parentDir = allDirectories.find(d => d.name === parentName);
      if (parentDir && !parents.includes(parentDir.id)) {
        parents.push(parentDir.id);
        // Recursively find this parent's parent
        return findParentsByName(parentDir, parents);
      }
      
      return parents;
    }
    
    // Combine all parent-finding methods
    const parentsByIdDirect = findParentsById(selectedDir);
    const parentsByPath = findParentsByPath(selectedDir);
    const parentsByName = findParentsByName(selectedDir);
    
    // Combine all parent IDs
    const allParentIds = [...parentsByIdDirect, ...parentsByPath, ...parentsByName];
    
    // Get the combined list of directories to expand (including the target directory)
    const existingExpanded = state.expandedDirectoryIds || [];
    const allExpanded = [...existingExpanded, directoryId, ...allParentIds];
    
    // Filter out duplicates
    const uniqueExpanded = [...new Set(allExpanded)];
    
    return { expandedDirectoryIds: uniqueExpanded };
  }),
  

  
  // Reset expanded directory state
  resetExpandedState: () => set(() => ({
    expandedDirectoryIds: []
  })),
  
  // Increment refresh counter to bust cache after uploads
  incrementRefreshCounter: () => set((state) => ({
    refreshCounter: state.refreshCounter + 1
  })),

  // Set loading state for search
  setLoadingSearch: (loading: boolean) => set({ loadingSearch: loading }),
}));
